import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialMode = location.pathname.includes("register") ? "register" : "login";

  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login({ email: email.trim(), password });
        toast.success("Welcome back");
      } else {
        await register({ email: email.trim(), password, name: name.trim() });
        toast.success("Account created");
      }
      navigate("/", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="app-shell min-h-screen flex items-center justify-center px-6 py-12">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "rgba(18,18,23,0.95)",
            color: "#F5F5F5",
            border: "1px solid rgba(212,175,55,0.25)",
            backdropFilter: "blur(12px)",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />

      <div className="w-full max-w-md fade-up">
        <div className="text-center mb-10">
          <div
            className="w-12 h-12 rounded-full mx-auto flex items-center justify-center mb-4"
            style={{
              background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(212,175,55,0.02))",
              border: "1px solid rgba(212,175,55,0.35)",
            }}
          >
            <span className="font-serif text-[#D4AF37] text-xl">M</span>
          </div>
          <p className="eyebrow">MF Reminder</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F5F5F5] mt-3 leading-tight">
            {mode === "login" ? (
              <>Welcome <span className="italic text-[#D4AF37]">back</span></>
            ) : (
              <>Create your <span className="italic text-[#D4AF37]">ledger</span></>
            )}
          </h1>
          <p className="text-[#8E8E93] mt-4 text-sm">
            {mode === "login"
              ? "Sign in to view your reservations."
              : "A private space for the moments that matter."}
          </p>
        </div>

        <div className="glass-card rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === "register" && (
              <div>
                <label className="eyebrow text-[10px] block mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-luxe"
                  placeholder="Your name"
                  data-testid="register-name-input"
                />
              </div>
            )}

            <div>
              <label className="eyebrow text-[10px] block mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-luxe"
                placeholder="you@example.com"
                required
                data-testid="auth-email-input"
              />
            </div>

            <div>
              <label className="eyebrow text-[10px] block mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-luxe"
                placeholder={mode === "register" ? "At least 6 characters" : "••••••••"}
                minLength={6}
                required
                data-testid="auth-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-gold rounded-full w-full px-6 py-3 text-xs tracking-[0.25em] uppercase disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              data-testid="auth-submit-btn"
            >
              {submitting
                ? "One moment…"
                : mode === "login"
                  ? "Sign in"
                  : "Create account"}
            </button>

            {mode === "login" && (
              <p className="text-center -mt-1">
                <Link
                  to="/forgot-password"
                  className="text-xs text-[#8E8E93] hover:text-[#D4AF37] transition-colors tracking-wide"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </p>
            )}
          </form>

          <div className="gold-hairline w-16 mx-auto my-6" />

          <p className="text-center text-sm text-[#8E8E93]">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors underline-offset-4 hover:underline"
              data-testid="auth-toggle-mode"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>

        <p className="text-center text-[10px] tracking-[0.3em] uppercase text-[#8E8E93]/70 mt-8">
          MF · Private Edition
        </p>
      </div>
    </div>
  );
}
